<template>
  <div class="ts">
    <section v-for="tm in list" v-show="!tm.hidden" :key="tm.key" :class="['ts-panel', 'team', tm.key]" :style="{ '--tc': tm.bright }">
      <div class="ts-head">
        <button class="ts-name" @click="$emit('team', tm.key)" v-tip="'Open the ' + tm.label + ' team card'">{{ tm.label }} <span class="ts-more">Team card ›</span></button>
        <span v-if="tm.units.length && gameData" class="lx-grade ts-grade" :style="{ background: teamG(tm).color }" v-tip="() => teamTip(tm.label, tm.units, { color: tm.bright, tg: teamG(tm) })">{{ teamG(tm).grade }}</span>
        <ChampStreakBadge v-if="tm.key === 'champion' && champStreak" :streak="champStreak" />
        <span class="ts-fate">{{ tm.fate }}</span>
      </div>
      <button v-for="(u, k) in tm.units" :key="k" class="ts-row" :data-player="(u.Name || '').toLowerCase()" :class="{ boss: u.RaidBoss }"
              @click="card = { unit: u, side: tm, foe: tm.foe }" v-tip="() => unitTip(u, { team: tm.label, color: tm.bright, grade: gameData ? unitG(u) : null, gd: gameData, foes: tm.foe && tm.foe.units, foeLabel: tm.foe && tm.foe.label, mates: tm.units, foot: 'Click for the full card' })">
        <UnitSprite :unit="u" :team-color="tm.pal" :fallback-src="portrait(u, tm.label)" :size="38" />
        <span v-if="gameData" class="lx-grade ts-g" :style="{ background: unitG(u).color }">{{ unitG(u).grade }}</span>
        <span class="ts-who">
          <b :style="trackColor(u) ? { color: trackColor(u) } : null"><span v-if="u.RaidBoss" class="ts-boss" v-tip="'Raid boss'">★</span>{{ u.Name }}</b>
          <i>{{ u.Gender === 'Monster' ? '' : u.Gender + ' ' }}{{ u.Class }} · {{ u.Sign }}</i>
        </span>
        <span class="ts-nums">HP {{ stat(u, 'hp') }} · Spd {{ stat(u, 'speed') }} · {{ u.Brave }}/{{ u.Faith }}</span>
        <span class="ts-kit">{{ [u.Mainhand, u.ActionSkill, u.SupportSkill, u.ReactionSkill].filter(Boolean).join(' · ') }}</span>
      </button>
      <p v-if="!tm.units.length" class="ts-empty">No roster yet.</p>
    </section>
    <UnitCardNew v-if="card && gameData" :unit="card.unit" :side="card.side" :foe="card.foe" :gd="gameData" :fallback="portrait(card.unit, card.side.label)"
                 @close="card = null" />
  </div>
</template>

<script>
// The homepage's team section (2026-10 refresh), under the bracket: every team in a brass-framed panel with its grade
// and how its tournament is going ("Lost the quarterfinal to Blue", "Next: the semifinal against Yellow"), each unit a
// row with its grade, class, sign, HP, Speed, Brave/Faith and kit. Click a unit for its full card (UnitCardNew, against
// the team it faces next, or last faced). The team's
// name opens its full team card (`team`). Panels keep the old grid's hooks: `.team.<name>` (Luxstadio's goto) and each row's
// `data-player` (gotoPlayer), and a team's `hidden` flag (the team buttons) hides its panel.
import UnitSprite from '../UnitSprite.vue';
import ChampStreakBadge from '../ChampStreakBadge.vue';
import UnitCardNew from '../spotlight/UnitCardNew.vue';
import { unitInfo } from '../spotlight/unitInfo.js';
import { matchColors } from '../../lib/spriteForClass.js';
import { teamBright } from '../../lib/teamColors.js';
import { UNITS_BASE } from '../../lib/assetUrls.js';
import { unitGrade, teamGrade } from '../../lib/unitGrade.js';
import { rosterStats } from '../../lib/preload.js';
import { unitTip, teamTip } from '../../lib/unitTip.js';
import { teamPath } from './teamPath.js';

export default {
  name: 'TeamSection',
  components: { UnitSprite, ChampStreakBadge, UnitCardNew },
  props: {
    teams: { type: Array, required: true },      // [{ name, label, roster, palettes, hidden }]
    winners: { type: Array, default: () => [] },
    gameData: { type: Object, default: null },
    champStreak: { type: Number, default: 0 },
    highlightColors: { type: [Map, Object], default: () => new Map() },
  },
  emits: ['team'],
  data() { return { card: null }; },
  computed: {
    byName() { const m = {}; for (const t of this.teams) m[t.name] = t; return m; },
    list() {
      const W = this.winners || [];
      const side = (t) => ({ key: t.name, label: t.label, units: t.roster || [], palettes: t.palettes, bright: teamBright(t.label) });
      const label = (n) => (n === 'champion' ? 'the Champion' : (this.byName[n] || {}).label || n);
      return this.teams.map((t) => {
        const tm = { ...side(t), hidden: t.hidden };
        [tm.pal] = matchColors(t, null);
        const path = teamPath(t.name, W, label);
        const o = path.foeKey && this.byName[path.foeKey];
        tm.foe = o ? side(o) : null;
        if (tm.foe) [tm.pal, tm.foe.pal] = matchColors(t, o);
        tm.fate = path.fate;
        return tm;
      });
    },
  },
  watch: { teams() { this._ug = null; this._tg = null; this._info = null; } },
  methods: {
    portrait(u, label) {
      if (u.Gender === 'Monster') return UNITS_BASE + u.Class + '.gif';
      const g = (u.Gender || 'Male')[0];
      return label === 'Champion' ? UNITS_BASE + u.Class + g + '.gif' : UNITS_BASE + u.Class + g + '_' + label + '.gif';
    },
    info(u) { this._info = this._info || new Map(); if (!this._info.has(u)) { let r = null; try { r = unitInfo(u, this.gameData); } catch (e) { r = null; } this._info.set(u, r); } return this._info.get(u); },
    stat(u, k) { const i = this.gameData && this.info(u); return i ? i.stats[k] : '–'; },
    unitTip, teamTip,
    unitG(u) { this._ug = this._ug || new Map(); if (!this._ug.has(u)) this._ug.set(u, unitGrade(u, rosterStats, this.gameData)); return this._ug.get(u); },
    teamG(tm) { this._tg = this._tg || new Map(); if (!this._tg.has(tm.units)) this._tg.set(tm.units, teamGrade(tm.units, rosterStats, this.gameData)); return this._tg.get(tm.units); },
    trackColor(u) { const h = this.highlightColors, n = (u.Name || '').toLowerCase(); return (h && n && (h instanceof Map ? h.get(n) : h[n])) || null; },
  },
};
</script>

<style scoped>
.ts { display: grid; grid-template-columns: repeat(auto-fill, minmax(420px, 1fr)); gap: 14px; margin: 8px 0 24px; text-align: left; }
@media (max-width: 480px) { .ts { grid-template-columns: minmax(0, 1fr); } }
.ts-panel { margin: 0; border: 1px solid var(--brass-edge); border-top: 3px solid var(--tc); border-radius: 4px; background: linear-gradient(180deg, var(--panel-top), var(--panel-bottom)); padding: 8px 10px; box-shadow: 0 4px 12px rgba(0,0,0,.45); color: var(--ink); scroll-margin-top: 60px; }
.ts-head { display: flex; align-items: baseline; gap: 8px; margin-bottom: 4px; }
.ts-name { padding: 0; border: 0; background: none; cursor: pointer; font-family: var(--cond); font-weight: 700; font-size: 18px; text-transform: uppercase; letter-spacing: 1px; color: var(--tc); text-shadow: 0 1px 0 #000; filter: brightness(1.3); }
.ts-name:hover .ts-more, .ts-name:focus-visible .ts-more { opacity: 1; }
.ts-more { font-size: 12px; letter-spacing: 0.6px; color: var(--brass); filter: none; opacity: 0.55; vertical-align: 2px; }
.ts-grade { font-size: 14px; }
.ts-fate { margin-left: auto; font-size: 12px; font-style: italic; color: var(--mute); text-align: right; }
.ts-row { width: 100%; display: grid; grid-template-columns: 40px 30px minmax(0, 1fr) auto; grid-template-rows: auto auto; column-gap: 8px; align-items: center; padding: 3px 0;
  background: none; border: 0; border-top: 1px solid rgba(255,255,255,.05); border-radius: 3px; font: inherit; color: inherit; text-align: left; cursor: pointer; scroll-margin-top: 80px; }
.ts-row:hover, .ts-row:focus-visible { background: rgba(217,180,94,.08); outline: none; }
.ts-row.flash { animation: ts-flash 2s ease-out; }
@keyframes ts-flash { 0%, 40% { background: rgba(243,214,142,.35); } 100% { background: none; } }
.ts-row > :first-child { grid-row: span 2; }
.ts-g { grid-row: span 2; justify-self: center; font-size: 13px; }
.ts-who { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ts-who b { font-family: var(--cond); font-weight: 700; font-size: 15px; color: var(--ink-hi); }
.ts-who i { font-style: normal; font-size: 12px; color: var(--mute); margin-left: 6px; }
.ts-boss { color: gold; margin-right: 2px; }
.ts-row.boss { box-shadow: inset 3px 0 0 gold; }
.ts-nums { font-size: 12px; white-space: nowrap; font-variant-numeric: tabular-nums; }
.ts-kit { grid-column: 3 / span 2; font-size: 11.5px; color: var(--mute); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ts-empty { margin: 4px 0; font-size: 12.5px; color: var(--mute); font-style: italic; }
</style>
